/**
 * ContractDetailModal
 * 전자계약 상세 모달
 */

import React, { useState, useEffect } from 'react';

interface ContractDetailModalProps {
  contractId: string;
  onClose: () => void;
}

interface Contract {
  id: string;
  contract_number: string;
  contract_type: string;
  title: string;
  status: string;
  signer_name: string;
  signer_phone: string;
  signer_email?: string;
  organization_name?: string;
  creator_name?: string;
  contract_data: {
    amount?: number;
    period?: string;
    plan_name?: string;
    terms?: string;
  };
  contract_html?: string;
  sign_token?: string;
  signature_image?: string;
  signature_ip?: string;
  document_hash?: string;
  expires_at?: string;
  sent_at?: string;
  signed_at?: string;
  completed_at?: string;
  created_at: string;
}

interface AuditLog {
  id: string;
  action: string;
  actor_type: string;
  actor_name?: string;
  old_status?: string;
  new_status?: string;
  ip_address?: string;
  created_at: string;
}

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  draft: { label: '초안', color: 'bg-gray-100 text-gray-800' },
  sent: { label: '발송됨', color: 'bg-blue-100 text-blue-800' },
  verified: { label: '인증완료', color: 'bg-yellow-100 text-yellow-800' },
  signed: { label: '서명완료', color: 'bg-green-100 text-green-800' },
  completed: { label: '완료', color: 'bg-emerald-100 text-emerald-800' },
  expired: { label: '만료', color: 'bg-red-100 text-red-800' },
  cancelled: { label: '취소', color: 'bg-gray-100 text-gray-600' },
};

const ACTION_LABELS: Record<string, string> = {
  created: '계약 생성',
  sent: '서명 요청 발송',
  verified: '본인 인증 완료',
  signed: '서명 완료',
  completed: '처리 완료',
  cancelled: '계약 취소',
};

export const ContractDetailModal: React.FC<ContractDetailModalProps> = ({
  contractId,
  onClose,
}) => {
  const [contract, setContract] = useState<Contract | null>(null);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'info' | 'logs'>('info');

  const fetchContract = async () => {
    try {
      const response = await fetch(`/api/admin/contracts/${contractId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
        },
      });

      if (!response.ok) throw new Error('계약 조회 실패');

      const data = await response.json();
      setContract(data.contract);
    } catch (err) {
      setError(err instanceof Error ? err.message : '오류 발생');
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      const response = await fetch(`/api/admin/contracts/${contractId}/audit-logs`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setAuditLogs(data.logs || []);
      }
    } catch (err) {
      console.error('감사 로그 조회 실패:', err);
    }
  };

  useEffect(() => {
    fetchContract();
    fetchAuditLogs();
  }, [contractId]);

  const handleSend = async () => {
    if (!window.confirm('서명 요청을 발송하시겠습니까?')) return;

    try {
      setActionLoading(true);
      const response = await fetch(`/api/admin/contracts/${contractId}/send`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
        },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || '발송 실패');
      }

      const data = await response.json();
      alert(`서명 요청이 발송되었습니다.\n\n서명 URL: ${data.sign_url}`);
      fetchContract();
      fetchAuditLogs();
    } catch (err) {
      alert(err instanceof Error ? err.message : '오류 발생');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    const reason = prompt('취소 사유를 입력해주세요:');
    if (reason === null) return;

    try {
      setActionLoading(true);
      const response = await fetch(`/api/admin/contracts/${contractId}/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
        },
        body: JSON.stringify({ reason }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || '취소 실패');
      }

      alert('계약이 취소되었습니다.');
      fetchContract();
      fetchAuditLogs();
    } catch (err) {
      alert(err instanceof Error ? err.message : '오류 발생');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await fetch(`/api/admin/contracts/${contractId}/download`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
        },
      });

      if (!response.ok) throw new Error('다운로드 실패');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contract_${contract?.contract_number}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err instanceof Error ? err.message : '오류 발생');
    }
  };

  const copySignUrl = () => {
    if (!contract?.sign_token) return;
    const url = `${window.location.origin}/contract/sign/${contract.sign_token}`;
    navigator.clipboard.writeText(url);
    alert('서명 URL이 클립보드에 복사되었습니다.');
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('ko-KR');
  };

  const formatAmount = (amount?: number) => {
    if (!amount) return '-';
    return amount.toLocaleString() + '원';
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">로딩 중...</div>
      </div>
    );
  }

  if (error || !contract) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <p className="text-red-600">{error || '계약을 찾을 수 없습니다.'}</p>
          <button onClick={onClose} className="mt-4 px-4 py-2 bg-gray-100 rounded">
            닫기
          </button>
        </div>
      </div>
    );
  }

  const statusInfo = STATUS_LABELS[contract.status] || {
    label: contract.status,
    color: 'bg-gray-100 text-gray-800',
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* 헤더 */}
        <div className="px-6 py-4 border-b flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold">{contract.title}</h2>
            <p className="text-sm text-gray-500">{contract.contract_number}</p>
          </div>
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1 rounded-full text-sm ${statusInfo.color}`}>
              {statusInfo.label}
            </span>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              &times;
            </button>
          </div>
        </div>

        {/* 탭 */}
        <div className="border-b flex">
          <button
            onClick={() => setActiveTab('info')}
            className={`px-6 py-3 font-medium ${
              activeTab === 'info'
                ? 'border-b-2 border-indigo-600 text-indigo-600'
                : 'text-gray-500'
            }`}
          >
            계약 정보
          </button>
          <button
            onClick={() => setActiveTab('logs')}
            className={`px-6 py-3 font-medium ${
              activeTab === 'logs'
                ? 'border-b-2 border-indigo-600 text-indigo-600'
                : 'text-gray-500'
            }`}
          >
            활동 로그
          </button>
        </div>

        {/* 내용 */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'info' ? (
            <div className="space-y-6">
              {/* 서명자 정보 */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold mb-3">서명자 정보</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">이름:</span>{' '}
                    <span className="font-medium">{contract.signer_name}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">연락처:</span>{' '}
                    <span className="font-medium">{contract.signer_phone}</span>
                  </div>
                  {contract.signer_email && (
                    <div>
                      <span className="text-gray-500">이메일:</span>{' '}
                      <span className="font-medium">{contract.signer_email}</span>
                    </div>
                  )}
                  {contract.organization_name && (
                    <div>
                      <span className="text-gray-500">조직:</span>{' '}
                      <span className="font-medium">{contract.organization_name}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* 계약 내용 */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold mb-3">계약 내용</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">유형:</span>{' '}
                    <span className="font-medium">
                      {contract.contract_type === 'subscription' ? '정기구독' : '일회성'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">플랜:</span>{' '}
                    <span className="font-medium">
                      {contract.contract_data?.plan_name || '-'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">월 이용료:</span>{' '}
                    <span className="font-medium text-indigo-600">
                      {formatAmount(contract.contract_data?.amount)}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">구독 방식:</span>{' '}
                    <span className="font-medium">월 정기결제</span>
                  </div>
                </div>
                {contract.contract_data?.terms && (
                  <div className="mt-4 text-sm">
                    <span className="text-gray-500">계약 조건:</span>
                    <pre className="mt-2 p-3 bg-white rounded border text-gray-700 whitespace-pre-wrap">
                      {contract.contract_data.terms}
                    </pre>
                  </div>
                )}
              </div>

              {/* 일정 */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold mb-3">일정</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">생성일:</span>{' '}
                    {formatDate(contract.created_at)}
                  </div>
                  <div>
                    <span className="text-gray-500">만료일:</span>{' '}
                    {formatDate(contract.expires_at)}
                  </div>
                  {contract.sent_at && (
                    <div>
                      <span className="text-gray-500">발송일:</span>{' '}
                      {formatDate(contract.sent_at)}
                    </div>
                  )}
                  {contract.signed_at && (
                    <div>
                      <span className="text-gray-500">서명일:</span>{' '}
                      {formatDate(contract.signed_at)}
                    </div>
                  )}
                </div>
              </div>

              {/* 서명 정보 (서명 완료 시) */}
              {contract.signature_image && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold mb-3">서명 정보</h3>
                  <div className="text-sm mb-3">
                    <div>
                      <span className="text-gray-500">서명자 IP:</span>{' '}
                      {contract.signature_ip || '-'}
                    </div>
                    {contract.document_hash && (
                      <div className="mt-2">
                        <span className="text-gray-500">문서 해시:</span>{' '}
                        <code className="text-xs bg-gray-200 px-1 rounded">
                          {contract.document_hash.slice(0, 32)}...
                        </code>
                      </div>
                    )}
                  </div>
                  <div>
                    <span className="text-gray-500">전자 서명:</span>
                    <img
                      src={contract.signature_image}
                      alt="서명"
                      className="mt-2 max-w-xs border rounded bg-white"
                    />
                  </div>
                </div>
              )}

              {/* 서명 URL (draft/sent 상태) */}
              {contract.sign_token && ['draft', 'sent'].includes(contract.status) && (
                <div className="bg-blue-50 rounded-lg p-4">
                  <h3 className="font-semibold mb-2">서명 URL</h3>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      readOnly
                      value={`${window.location.origin}/contract/sign/${contract.sign_token}`}
                      className="flex-1 px-3 py-2 border rounded bg-white text-sm"
                    />
                    <button
                      onClick={copySignUrl}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      복사
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {auditLogs.length === 0 ? (
                <p className="text-gray-500 text-center py-8">활동 로그가 없습니다.</p>
              ) : (
                auditLogs.map((log) => (
                  <div
                    key={log.id}
                    className="flex items-start gap-4 p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="w-2 h-2 mt-2 rounded-full bg-indigo-500" />
                    <div className="flex-1">
                      <div className="font-medium">
                        {ACTION_LABELS[log.action] || log.action}
                      </div>
                      <div className="text-sm text-gray-500">
                        {log.actor_type === 'admin' && '관리자'}
                        {log.actor_type === 'signer' && '서명자'}
                        {log.actor_type === 'system' && '시스템'}
                        {log.actor_name && ` (${log.actor_name})`}
                        {log.ip_address && ` · IP: ${log.ip_address}`}
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                        {formatDate(log.created_at)}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* 푸터 액션 */}
        <div className="px-6 py-4 border-t bg-gray-50 flex justify-between">
          <div className="flex gap-2">
            {contract.status === 'draft' && (
              <button
                onClick={handleSend}
                disabled={actionLoading}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                서명 요청 발송
              </button>
            )}
            {['draft', 'sent', 'verified'].includes(contract.status) && (
              <button
                onClick={handleCancel}
                disabled={actionLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                계약 취소
              </button>
            )}
            {['signed', 'completed'].includes(contract.status) && (
              <button
                onClick={handleDownload}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                PDF 다운로드
              </button>
            )}
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded-lg hover:bg-gray-100"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContractDetailModal;
